import React, { useState } from 'react';
import { DownloadIcon, MailIcon, PhoneIcon, PlayIcon, XIcon } from 'lucide-react';
import { FaInstagram, FaSpotify, FaTiktok, FaYoutube } from 'react-icons/fa';
import type { WebsiteContent } from '../../data/website';
interface EpkExperienceProps {
  content: WebsiteContent;
}
const socialPlatforms = [{
  key: 'instagramUrl',
  label: 'Instagram',
  icon: FaInstagram
}, {
  key: 'tiktokUrl',
  label: 'TikTok',
  icon: FaTiktok
}, {
  key: 'spotifyUrl',
  label: 'Spotify',
  icon: FaSpotify
}, {
  key: 'youtubeUrl',
  label: 'YouTube',
  icon: FaYoutube
}] as const;

/**
 * The press kit at /epk. Built for bookers and press rather than fans: the
 * facts, the quotes, the photos, and one obvious way to make contact. It is
 * deliberately unlinked from the public site navigation.
 */
export function EpkExperience({
  content
}: EpkExperienceProps) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const facts = [{
    label: 'Genre',
    value: content.seoGenre
  }, {
    label: 'Based in',
    value: content.seoLocation
  }, {
    label: 'Origin',
    value: 'College Station, Texas'
  }, {
    label: 'Notable stage',
    value: content.epkFactDraw
  }].filter(fact => fact.value.trim());
  const award = content.aboutAwards[0];
  const paragraphs = content.epkBio.split('\n').filter(line => line.trim());
  return <main className="min-h-screen w-full overflow-x-hidden bg-[#100d0b] text-[#fffdf8]" data-id="element-1012" data-mp="2f0yww03uvk8j">
      <header className="relative isolate flex min-h-[72vh] items-center justify-center overflow-hidden" data-id="element-1013" data-mp="219u5flan5y9a">
        <img src={content.epkHeroImageUrl || content.heroImageUrl} alt={content.epkHeroImageAlt || `${content.bandName} performing live`} className="absolute inset-0 -z-20 h-full w-full object-cover object-[52%_45%]" data-id="element-1014" data-mp="263g7kjj6ijqu" />
        <div className="absolute inset-0 -z-10 bg-black/55" data-id="element-1015" data-mp="1ic142nfo1we0" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-t from-[#100d0b] to-transparent" data-id="element-1016" data-mp="1iewyw2w7w4y8" />

        <div className="relative z-10 mx-auto w-full max-w-5xl px-5 py-20 text-center sm:px-8" data-id="element-1017" data-mp="3p0tajhud24kf">
          {content.epkEyebrow.trim() && <p className="mb-6 text-[10px] font-semibold uppercase tracking-[0.32em] text-[#f0d5bb] sm:text-[11px]" data-id="element-1018" data-mp="3k91q7p1ssv2q">
              {content.epkEyebrow}
            </p>}
          <h1 className="mx-auto max-w-4xl font-serif-display text-[48px] font-bold uppercase leading-[0.88] tracking-[0.07em] text-white drop-shadow-[0_3px_20px_rgba(0,0,0,0.7)] sm:text-[74px] lg:text-[90px]" data-id="element-1019" data-mp="3acf6j3l7ka2a">
            {content.bandName}
          </h1>
          <div className="mt-8 flex items-center justify-center gap-5" data-id="element-1020" data-mp="6t3rnfqwugtc">
            {socialPlatforms.map(({
            key,
            label,
            icon: Icon
          }) => {
            const url = content[key];
            return url ? <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={`Open ${label}`} className="text-white transition-colors duration-150 ease-out hover:text-[#f2b77f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a369]" data-id="element-1021" data-mp="2mmckb43z5ybl">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-1022" data-mp="121xoa42lryxh" />
                </a> : null;
          })}
          </div>
        </div>
      </header>

      {facts.length > 0 && <section aria-label="Band at a glance" className="border-y border-white/[0.1] bg-[#0c0a09]" data-id="element-1023" data-mp="1bw6f8nbzjjtj">
          <dl className="mx-auto grid max-w-5xl grid-cols-2 gap-px bg-white/[0.07] sm:grid-cols-4" data-id="element-1024" data-mp="1gvapziek5plz">
            {facts.map(fact => <div key={fact.label} className="bg-[#0c0a09] px-5 py-6" data-id="element-1025" data-mp="1ptcrdz27ek9s">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#c9895c]" data-id="element-1026" data-mp="1usz6yf5i8clr">{fact.label}</dt>
                <dd className="mt-2 text-[15px] font-medium leading-snug text-white sm:text-[17px]" data-id="element-1027" data-mp="3rlr9oz8ntqqq">{fact.value}</dd>
              </div>)}
          </dl>
        </section>}

      {content.epkQuotes.length > 0 && <section aria-label="What other artists are saying" className="px-5 py-16 sm:px-8 sm:py-20" data-id="element-1028" data-mp="318umgpd3myjj">
          <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-3" data-id="element-1029" data-mp="2wa9m8pxukev3">
            {content.epkQuotes.map(quote => <figure key={quote.id} className="flex flex-col" data-id="element-1030" data-mp="3b692woz0vnbu">
                <blockquote className="font-serif-display text-[24px] leading-[1.25] text-[#f4d4b8] sm:text-[27px]" data-id="element-1031" data-mp="36aa9uo2r1h53">
                  “{quote.quote}”
                </blockquote>
                <figcaption className="mt-auto flex items-center gap-3 pt-6" data-id="element-1032" data-mp="2dl387g800bfm">
                  {quote.imageUrl && <img src={quote.imageUrl} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover grayscale" data-id="element-1033" data-mp="2saiw8og5a2rm" />}
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white" data-id="element-1034" data-mp="1zrc2yk1avdpp">
                    {quote.artist}
                  </span>
                </figcaption>
              </figure>)}
          </div>
        </section>}

      <section aria-labelledby="epk-bio" className="border-t border-white/[0.08] px-5 py-16 sm:px-8 sm:py-20" data-id="element-1035" data-mp="f3t2qmmgqikx">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" data-id="element-1036" data-mp="f6bbmjka0dc">
          <div data-id="element-1037" data-mp="5dhnw518qoz9">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#e8a369]" data-id="element-1038" data-mp="5d7zglvugpvo">Biography</p>
            <h2 id="epk-bio" className="mt-3 font-serif-display text-[38px] leading-[1.05] text-white sm:text-[46px]" data-id="element-1039" data-mp="t0g4d2pd1wx6">
              {content.epkBioTitle}
            </h2>
          </div>
          <div className="font-serif-text space-y-4 text-[16px] leading-[1.65] text-zinc-300" data-id="element-1040" data-mp="k1kxdoehsist">
            {paragraphs.map((paragraph, index) => <p key={index} data-id="element-1041" data-mp="34rjzvicrpzo1" data-mpi={index}>{paragraph}</p>)}
          </div>
        </div>
      </section>

      {content.epkPressPhotos.length > 0 && <section aria-labelledby="epk-photos" className="border-t border-white/[0.08] px-5 py-16 sm:px-8 sm:py-20" data-id="element-1042" data-mp="38uzru1dw7bue">
          <div className="mx-auto max-w-5xl" data-id="element-1043" data-mp="1fcsh9zemi8ty">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-3" data-id="element-1044" data-mp="1acweu4lkqpgi">
              <h2 id="epk-photos" className="font-serif-display text-[38px] leading-none text-white sm:text-[46px]" data-id="element-1045" data-mp="15kmur5754jm5">
                Press photos
              </h2>
              <p className="text-[12px] text-zinc-500" data-id="element-1046" data-mp="10k7eo5vh4y0h">Click any photo to view full size · right-click to save</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4" data-id="element-1047" data-mp="rku419vm63e6">
              {content.epkPressPhotos.map((photo, index) => <figure key={photo.id} className="group" data-id="element-1048" data-mp="mmsj0jemnu02" data-mpi={index}>
                  <button type="button" onClick={() => setLightbox(index)} className="block w-full overflow-hidden rounded-sm border border-white/[0.1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e8a369]" data-id="element-1049" data-mp="hnpjfehf2fzh">
                    <img src={photo.url} alt={photo.caption || `${content.bandName} press photo ${index + 1}`} className="h-56 w-full object-cover transition-transform duration-200 ease-out group-hover:scale-[1.03] sm:h-64" data-id="element-1050" data-mp="2kh66b05o87i3" />
                  </button>
                  {photo.caption && <figcaption className="mt-2 text-[11.5px] leading-relaxed text-zinc-500" data-id="element-1051" data-mp="21jv7fgnybg36">{photo.caption}</figcaption>}
                </figure>)}
            </div>
          </div>
        </section>}

      {content.epkTracks.length > 0 && <section aria-labelledby="epk-music" className="border-t border-white/[0.08] px-5 py-16 sm:px-8 sm:py-20" data-id="element-1052" data-mp="2fj4n8f88w86m">
          <div className="mx-auto max-w-5xl" data-id="element-1053" data-mp="3lzgm6iiw5u39">
            <h2 id="epk-music" className="mb-8 font-serif-display text-[38px] leading-none text-white sm:text-[46px]" data-id="element-1054" data-mp="3lyoq76ldtucg">
              Music
            </h2>
            <ol className="border-y border-white/[0.13]" data-id="element-1055" data-mp="fwg1yay71354">
              {content.epkTracks.map((track, index) => <li key={track.id} className="flex items-center gap-4 border-b border-white/[0.08] py-4 last:border-b-0" data-id="element-1056" data-mp="bwe0a1sgk9pu" data-mpi={index}>
                  <span className="w-6 shrink-0 text-[13px] tabular-nums text-zinc-600" data-id="element-1057" data-mp="kwaqi7uhxgn0">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-[17px] font-medium text-white" data-id="element-1058" data-mp="zkfcplsrcaaj">{track.title}</span>
                  {track.duration && <span className="shrink-0 text-[13px] tabular-nums text-zinc-500" data-id="element-1059" data-mp="ulc8u4oika45">{track.duration}</span>}
                  {track.url ? <a href={track.url} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-white/[0.2] px-3.5 py-2 text-[11.5px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 ease-out hover:border-[#e8a369] hover:bg-[#e8a369] hover:text-[#160e0a]" data-id="element-1060" data-mp="13mirajaq3v01">
                      <PlayIcon className="h-3 w-3" data-id="element-1061" data-mp="1ptmmra2qezt7" /> Play
                    </a> : <span className="shrink-0 text-[11.5px] uppercase tracking-[0.06em] text-zinc-600" data-id="element-1062" data-mp="1c3185xkb9c14">Unreleased</span>}
                </li>)}
            </ol>
          </div>
        </section>}

      {content.aboutVenues.length > 0 && <section aria-labelledby="epk-live" className="border-t border-white/[0.08] bg-[#0c0a09] px-5 py-16 sm:px-8 sm:py-20" data-id="element-1063" data-mp="175hjs4twfivg">
          <div className="mx-auto max-w-5xl" data-id="element-1064" data-mp="t6hx32py5e1x">
            <h2 id="epk-live" className="font-serif-display text-[38px] leading-none text-white sm:text-[46px]" data-id="element-1065" data-mp="x5qpa35c93vd">
              Live performances
            </h2>
            <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-zinc-400" data-id="element-1066" data-mp="2ngljfktfc7r3">{content.aboutVenuesIntro}</p>
            <ul className="mt-8 grid gap-px bg-white/[0.07] sm:grid-cols-2" data-id="element-1067" data-mp="2rhynpqgvc2vw">
              {content.aboutVenues.map(venue => <li key={venue.id} className="bg-[#0c0a09] px-5 py-4" data-id="element-1068" data-mp="1phzdeyg787zp">
                  <p className="text-[17px] font-medium text-white" data-id="element-1069" data-mp="1asthpkgkkzwk">{venue.venue}</p>
                  <p className="mt-1 text-[12.5px] text-zinc-500" data-id="element-1070" data-mp="1aqytnxa04ro9">
                    {[venue.detail, venue.city].filter(Boolean).join(' · ')}
                  </p>
                </li>)}
            </ul>

            {award && <div className="mt-10 flex flex-col items-center gap-4 border-t border-white/[0.1] pt-10 text-center sm:flex-row sm:justify-center" data-id="element-1071" data-mp="28gttcarefehg">
                <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#c9895c]" data-id="element-1072" data-mp="1uivel4961g1f">
                  {award.label}
                </span>
                <p className="font-serif-display text-[26px] leading-tight text-white sm:text-[30px]" data-id="element-1073" data-mp="j0kkgecxo3ix">{award.title}</p>
                {award.logoUrl && <img src={award.logoUrl} alt="" className="h-7 w-auto object-contain" data-id="element-1074" data-mp="n14gtfpon340" />}
              </div>}
          </div>
        </section>}

      <section aria-labelledby="epk-contact" className="border-t border-white/[0.08] px-5 py-16 text-center sm:px-8 sm:py-24" data-id="element-1075" data-mp="ajle7hf0idvz">
        <div className="mx-auto max-w-xl" data-id="element-1076" data-mp="pfjftf7pgafl">
          <h2 id="epk-contact" className="font-serif-display text-[38px] leading-none text-white sm:text-[46px]" data-id="element-1077" data-mp="kjucd0ske9e0">
            Booking
          </h2>
          {content.epkContactName.trim() && <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-zinc-500" data-id="element-1078" data-mp="3o0knfu307cmk">{content.epkContactName}</p>}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3" data-id="element-1079" data-mp="6kkw614czfa1">
            {content.epkContactEmail.trim() && <a href={`mailto:${content.epkContactEmail}`} className="inline-flex items-center gap-2 rounded-sm bg-[#d97742] px-5 py-3 text-[12px] font-bold uppercase tracking-[0.07em] text-[#1a0f09] transition-colors duration-150 ease-out hover:bg-[#e8a369]" data-id="element-1080" data-mp="3a84rtecfmgvo">
                <MailIcon className="h-3.5 w-3.5" data-id="element-1081" data-mp="2t4sfz6zc3jc" /> {content.epkContactEmail}
              </a>}
            {content.epkContactPhone.trim() && <a href={`tel:${content.epkContactPhone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-2 rounded-sm border border-white/[0.2] px-5 py-3 text-[12px] font-bold uppercase tracking-[0.07em] text-white transition-colors duration-150 ease-out hover:border-[#e8a369] hover:text-[#f2b77f]" data-id="element-1082" data-mp="7kv7gigv3fh3">
                <PhoneIcon className="h-3.5 w-3.5" data-id="element-1083" data-mp="3kyq4pbl5ce6g" /> {content.epkContactPhone}
              </a>}
          </div>
          {content.siteUrl.trim() && <p className="mt-8 text-[12.5px] text-zinc-600" data-id="element-1084" data-mp="108r3dkylx3ct">
              <a href="/" className="underline-offset-4 transition-colors hover:text-[#f2b77f] hover:underline" data-id="element-1085" data-mp="1088unourcwo0">
                {content.siteUrl.replace(/^https?:\/\//, '')}
              </a>
            </p>}
        </div>
      </section>

      {lightbox !== null && content.epkPressPhotos[lightbox] && <div role="dialog" aria-modal="true" aria-label="Press photo" className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-5" onClick={() => setLightbox(null)} data-id="element-1086" data-mp="2ksw6opnjuhhj">
          <button type="button" onClick={() => setLightbox(null)} aria-label="Close photo" className="absolute right-5 top-5 rounded-full border border-white/20 p-2 text-white transition-colors duration-150 ease-out hover:border-[#e8a369] hover:text-[#f2b77f]" data-id="element-1087" data-mp="26zobyp4695vm">
            <XIcon className="h-5 w-5" data-id="element-1088" data-mp="1s3oxmwqgy9i1" />
          </button>
          <img src={content.epkPressPhotos[lightbox].url} alt={content.epkPressPhotos[lightbox].caption || 'Press photo'} className="max-h-[80vh] max-w-full object-contain" data-id="element-1089" data-mp="3rfrpau98ya6s" />
          <div className="mt-4 flex items-center gap-4 text-[12.5px] text-zinc-400" data-id="element-1090" data-mp="3uhw6iwmxi4aj">
            <span data-id="element-1091" data-mp="3atfq6b0600u">{content.epkPressPhotos[lightbox].caption}</span>
            <a href={content.epkPressPhotos[lightbox].url} download target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()} className="inline-flex items-center gap-1.5 text-[#e8a369] underline-offset-4 hover:underline" data-id="element-1092" data-mp="3875u00n38fg">
              <DownloadIcon className="h-3.5 w-3.5" data-id="element-1093" data-mp="3ghbqu9cb8ol9" /> Download
            </a>
          </div>
        </div>}
    </main>;
}